import React, { useState, useEffect } from 'react';
import { Hash, SquareSlash } from 'lucide-react';
import { Tag as TagType } from '../../services/tags';
import './styles.css';
import { useTranslation } from 'react-i18next';
// import { useAuth } from '../../context/AuthContext';

interface TagTreeProps {
  onSelectTag: (tag: string | null) => Promise<void>;
  tags: TagType[];
  onTagsUpdate: () => Promise<void>;
}

interface TagNode {
  name: string;
  count: number;
  children: { [key: string]: TagNode };
  level2?: string;
  level3?: string;
}

export default function TagTree({ onSelectTag, tags, onTagsUpdate }: TagTreeProps) {
  const { t } = useTranslation('tagtree');
  // const { user } = useAuth();
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [tagTree, setTagTree] = useState<{ [key: string]: TagNode }>({});
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // 计算所有笔记的总数
  const totalNotes = tags.reduce((sum, tag) => sum + tag.count, 0);

  // 处理"所有笔记"点击
  const handleAllNotesClick = async () => {
    // console.log('all notes clicked');
    setSelectedTag(null);
    
    await onSelectTag(null);
    await onTagsUpdate();
  };

  useEffect(() => {
    // 将标签数组转换为树形结构
    const tree: { [key: string]: TagNode } = {};
    
    tags.forEach(tag => {
      const { level1, level2, level3, count } = tag;
      
      // 创建或获取一级节点
      if (!tree[level1]) {
        tree[level1] = { name: level1, count: 0, children: {} };
      }
      
      if (level2) {
        // 创建或获取二级节点
        if (!tree[level1].children[level2]) {
          tree[level1].children[level2] = { name: level2, count: 0, children: {} };
        }
        
        if (level3) {
          // 创建三级节点
          if (!tree[level1].children[level2].children[level3]) {
            tree[level1].children[level2].children[level3] = { 
              name: level3, 
              count, 
              children: {} 
            };
          }
          // 累加到父级节点
          tree[level1].children[level2].count += count;
          tree[level1].count += count;
        } else {
          // 二级标签的计数
          tree[level1].children[level2].count += count;
          tree[level1].count += count;
        }
      } else {
        // 一级标签的计数
        tree[level1].count += count;
      }
    });

    setTagTree(tree);
  }, [tags]);

  const handleTagClick = async (path: string[]) => {
    const tagPath = path.join('/');
    // console.log('Tag clicked:', tagPath);
    setSelectedTag(tagPath);
    
    try {
      // console.log('Calling onSelectTag with:', tagPath);
      await onSelectTag(tagPath);
      // console.log('onSelectTag completed');
      
      // console.log('Calling onTagsUpdate');
      await onTagsUpdate();
      // console.log('onTagsUpdate completed');
    } catch (error) {
      console.error('Error in handleTagClick:', error);
    }
  };

  const toggleExpand = (path: string[], e: React.MouseEvent) => {
    e.stopPropagation(); // 防止触发标签选择
    const nodePath = path.join('/');
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodePath)) {
        next.delete(nodePath);
      } else {
        next.add(nodePath);
      }
      return next;
    });
  };

  const renderTagNode = (node: TagNode, path: string[] = [], level: number = 0) => {
    const currentPath = [...path, node.name];
    const tagPath = currentPath.join('/');
    const isSelected = selectedTag === tagPath;
    const hasChildren = Object.keys(node.children).length > 0;
    const isExpanded = expandedNodes.has(tagPath);

    return (
      <div key={tagPath} style={{ marginLeft: `${level * 20}px` }}>
        <div 
          className={`tag-item ${isSelected ? 'active' : ''}`}
          onClick={() => handleTagClick(currentPath)}
        >
          {hasChildren && (
            <span 
              className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
              onClick={(e) => toggleExpand(currentPath, e)}
            >
              {isExpanded ? '▼' : '▶'}
            </span>
          )}
          {level === 0 ? (
            <Hash size={14} className="tag-icon" />
          ) : (
            <SquareSlash size={14} className="tag-icon" />
          )}
          <span className="tag-name">{node.name}</span>
          <span className="tag-count">{node.count}</span>
        </div>
        {isExpanded && hasChildren && (
          <div className="tag-children">
            {Object.entries(node.children).map(([childName, childNode]) => 
              renderTagNode(childNode, currentPath, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="tag-tree" style={{ overflowY: 'auto', maxHeight: '100%', WebkitOverflowScrolling: 'touch' }}>
      {/* 添加"所有笔记"选项 */}
      <div 
          className={`tag-item all-notes ${selectedTag === null ? 'active' : ''}`}
          onClick={() => handleAllNotesClick()} 
          style={{ cursor: 'pointer' }}
        >
          <span className="tag-name">{t('tagtree.all_notes')}</span>
          <span className="tag-count">{totalNotes}</span>
        </div>
      <h4>{t('tagtree.tag_tree')}</h4>
      <div className="tag-list">
        {/* 原有的标签树渲染 */}
        {Object.entries(tagTree).map(([tagName, node]) => 
          renderTagNode(node)
        )}
      </div>
    </div>
  );
} 